<template>
  <section class="Testimonials">
    <p class="Testimonials__eyebrow">Testimonials</p>
    <h2 class="Testimonials__heading">What Athletes are Saying</h2>
    <div class="flex items-center max-w-xl gap-4 mx-auto mb-12">
      <img src="~images/icons/stars.svg" alt="5 out of 5 stars" loading="lazy" class="w-14" />
      <p class="Testimonials__subtitle font-mono text-xs">4.9 / 5</p>
      <p class="Testimonials__subtitle">Real feedback from cyclists who train with FormBeat</p>
    </div>

    <div class="Testimonials__cols max-w-5xl mx-auto">
      <div v-for="(col, ci) in columns" :key="ci" class="Testimonials__col">
        <figure v-for="(t, ti) in col" :key="ti" class="Testimonials__card">
          <figcaption class="flex items-center mb-4">
            <img v-if="t.image" loading="lazy" class="object-cover w-12 h-12 mr-3 rounded-full" :src="t.image | imageUrl" :alt="t.name" />
            <div>
              <div class="Testimonials__name">{{ t.name }}</div>
              <div class="Testimonials__role">{{ t.role }}</div>
            </div>
          </figcaption>

          <blockquote class="Testimonials__quote">{{ t.quote }}</blockquote>
        </figure>
      </div>
    </div>
  </section>
</template>

<script>
const avatar1 = require('images/testimonials/1.jpg')
const avatar2 = require('images/testimonials/2.jpg')
const avatar3 = require('images/testimonials/3.jpg')

export default {
  name: 'Testimonials',

  data() {
    return {
      columnCount: 3,
      testimonials: [
        {
          name: 'Roly',
          role: 'Road racer',
          quote: 'I have been waiting for something like this.'
        },
        {
          name: 'Mark',
          role: 'Road cyclist',
          quote: 'FormBeat is my go-to site to analyze my rides and the first thing I check after a ride.'
        },
        {
          name: 'Eric B.',
          role: 'Endurance athlete',
          image: avatar1,
          quote: 'The best app I’ve found for making sense of my training data, and easily the simplest to actually understand.'
        },
        {
          name: 'Jeff',
          image: avatar3,
          role: 'Competitive Cyclist',
          quote: 'I still have Intervals.icu, but FormBeat is so much easier to understand — cleaner, more modern, and it just tells me what actually matters.'
        },
        {
          name: 'Stanley G.',
          role: 'Triathlete',
          image: avatar2,
          quote:
            'By far my favorite app for analyzing my training. The feedback on my intervals and efficiency is spot on, and it tells me exactly what to work on next - VO2, threshold, zone 2.'
        },
        {
          name: 'David',
          role: 'Cycling Enthusiast',
          quote:
            'I came to FormBeat disillusioned with Strava, which had become more about the social feed than real insight. This is clear, serious analysis without the complexity of other tools.'
        },
        {
          name: 'Rob',
          role: 'Cyclist',
          quote:
            'I was really impressed and think it is a great product. The use of CP and W’ adds a lot of value and lets this platform stand out compared to others.'
        },
        {
          name: 'Leon C.',
          role: '42 years, cycling enthusiast',
          quote:
            'Finally I understand the why behind every ride. The reasoning before and the feedback after show me exactly where I am in my plan and it matches how my legs actually feel.'
        },
        {
          name: 'Gert van de Poll',
          role: 'App Store review',
          quote: 'Connects straight to Strava and finally puts meaning behind the numbers. The AI chat is genuinely useful - well worth the few bucks a month.'
        }
      ]
    }
  },

  computed: {
    // Distribute cards greedily into the shortest column (estimated by quote length) so the
    // columns finish at roughly even heights — avoids the ragged gaps a CSS-columns masonry leaves.
    columns() {
      const cols = Array.from({ length: this.columnCount }, () => ({ items: [], height: 0 }))
      this.testimonials.forEach((t) => {
        const shortest = cols.reduce((min, c) => (c.height < min.height ? c : min), cols[0])
        shortest.items.push(t)
        shortest.height += (t.quote ? t.quote.length : 0) + 120
      })
      return cols.map((c) => c.items)
    }
  },

  mounted() {
    this.updateColumnCount()
    window.addEventListener('resize', this.updateColumnCount, { passive: true })
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateColumnCount)
  },

  methods: {
    updateColumnCount() {
      const w = window.innerWidth
      this.columnCount = w >= 1024 ? 3 : w >= 768 ? 2 : 1
    }
  }
}
</script>

<style lang="postcss" scoped>
/* Self-contained full-width dark section so the frosted cards read correctly on any page. */
.Testimonials {
  @apply w-full px-8 py-16;
  background: #001116;
}

/* Balanced masonry: columns are JS-distributed by height (see `columns` computed), each a
   flex stack — so they pack tightly AND finish at roughly even heights. */
.Testimonials__cols {
  @apply flex items-start;
  gap: 1.5rem;
}
.Testimonials__col {
  @apply flex flex-col flex-1;
  gap: 1.5rem;
  min-width: 0;
}

.Testimonials__eyebrow {
  @apply mb-3 text-sm font-bold tracking-wider text-center text-white uppercase opacity-75;
}
.Testimonials__heading {
  @apply xl:text-5xl px-6 mb-4 text-4xl font-bold leading-tight text-center text-white;
}
.Testimonials__subtitle {
  @apply text-gray-400;
}
.Testimonials__name {
  @apply font-bold leading-tight text-white;
}
.Testimonials__role {
  @apply text-sm text-gray-600;
}
.Testimonials__quote {
  @apply leading-relaxed text-gray-400;
}
.Testimonials__card {
  @apply p-6;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  transition: background 0.25s ease, border-color 0.25s ease, transform 0.25s ease;
}
.Testimonials__card:hover {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.18);
  transform: translateY(-2px);
}
</style>
